import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Modal,
  Tag,
  Badge,
  Statistic,
  Typography,
  Space,
  Empty,
  Tabs,
  List,
  Descriptions,
  Divider,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  StarFilled,
  EyeOutlined,
  BuildOutlined,
  WifiOutlined,
  CarOutlined,
  CoffeeOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  AppstoreOutlined,
  AppstoreAddOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../constants/apiPath';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const DEFAULT_HOTEL_IMAGE = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200';

const Hotels = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const { getAgentHotelsDetails } = useAuth();

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const result = await getAgentHotelsDetails();
        console.log('Hotels API Result:', result);
        if (result.success && result.data) {
          setHotels(result.data);
        } else {
          console.log('Failed to fetch hotels:', result);
        }
      } catch (error) {
        console.error('Failed to fetch hotels:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHotels();
  }, [getAgentHotelsDetails]);

  const filteredHotels = hotels.filter(hotel => {
    const name = hotel.Hotel_name || hotel.name || hotel.hotel_name || '';
    const location = (hotel.address || '') + ' ' + (hotel.city || '');
    const searchLow = searchTerm.toLowerCase();
    return name.toLowerCase().includes(searchLow) || location.toLowerCase().includes(searchLow);
  });

  const getRoomStats = (hotel) => {
    let totalRooms = 0;
    let availableRooms = 0;
    if (hotel.floors && Array.isArray(hotel.floors)) {
      hotel.floors.forEach(floor => {
        if (floor.rooms && Array.isArray(floor.rooms)) {
          totalRooms += floor.rooms.length;
          availableRooms += floor.rooms.filter(room => room.available).length;
        }
      });
    }
    return { totalRooms, availableRooms };
  };

  const renderStars = (rating) => {
    const stars = Math.floor(rating || 0);
    return (
      <Space size={2}>
        {[...Array(5)].map((_, i) => (
          <StarFilled key={i} style={{ color: i < stars ? '#faad14' : '#d9d9d9', fontSize: 12 }} />
        ))}
      </Space>
    );
  };

  const getAmenityIcon = (amenity) => {
    const low = amenity.toLowerCase();
    if (low.includes('wifi')) return <WifiOutlined />;
    if (low.includes('parking') || low.includes('car')) return <CarOutlined />;
    if (low.includes('breakfast') || low.includes('coffee')) return <CoffeeOutlined />;
    if (low.includes('24') || low.includes('clock')) return <ClockCircleOutlined />;
    return <InfoCircleOutlined />;
  };

  const getHotelPrimaryImage = (hotel) => {
    const topImage = Array.isArray(hotel?.HotelImages) && hotel.HotelImages.length > 0
      ? hotel.HotelImages[0]
      : null;

    if (!topImage) return DEFAULT_HOTEL_IMAGE;

    // Handles string path or object payloads from different APIs
    const imagePath =
      typeof topImage === 'string'
        ? topImage
        : topImage?.url || topImage?.path || topImage?.image || '';

    return imagePath ? getImageUrl(imagePath) : DEFAULT_HOTEL_IMAGE;
  };

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100%' }}>
      <div style={{ marginBottom: 32 }}>
        <Title level={2} style={{ marginBottom: 8 }}>Associated Hotels</Title>
        <Paragraph type="secondary">View and manage all hotel properties associated with your agent account.</Paragraph>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Input
          placeholder="Search hotels by name, city, or address..."
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          size="large"
          allowClear
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ maxWidth: 500, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
        />
      </div>

      {loading ? (
        <Row gutter={[24, 24]}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Col key={i} xs={24} sm={12} lg={8}>
              <Card loading active cover={<div style={{ height: 200, background: '#e8e8e8' }} />} />
            </Col>
          ))}
        </Row>
      ) : (
        <>
          <Row gutter={[24, 24]}>
            {filteredHotels.map(hotel => {
              const { totalRooms, availableRooms } = getRoomStats(hotel);
              const hotelImage = getHotelPrimaryImage(hotel);

              return (
                <Col key={hotel._id || hotel.Hotel_Id} xs={24} sm={12} lg={8}>
                  <Badge.Ribbon
                    text={hotel.status === 1 ? "Active" : "Inactive"}
                    color={hotel.status === 1 ? "green" : "red"}
                  >
                    <Card
                      hoverable
                      style={{ borderRadius: 12, overflow: 'hidden', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                      cover={
                        <div style={{ height: 200, overflow: 'hidden', position: 'relative' }}>
                          <img
                            alt={hotel.Hotel_name}
                            src={hotelImage}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={e => { e.target.src = DEFAULT_HOTEL_IMAGE; }}
                          />
                          {hotel.hotelType && (
                            <Tag color="blue" style={{ position: 'absolute', top: 12, left: 12, margin: 0 }}>
                              {hotel.hotelType}
                            </Tag>
                          )}
                        </div>
                      }
                      actions={[
                        <Button
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() => setSelectedHotel(hotel)}
                          key="view"
                        >
                          View Details
                        </Button>
                      ]}
                    >
                      <Card.Meta
                        title={<span style={{ fontSize: 18 }}>{hotel.Hotel_name}</span>}
                        description={
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text type="secondary" ellipsis={{ tooltip: true }}>
                              <EnvironmentOutlined style={{ marginRight: 4 }} />
                              {hotel.address}, {hotel.city}
                            </Text>
                            <Space size={8} wrap>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                <BuildOutlined style={{ marginRight: 4 }} />
                                ID: {hotel.Hotel_Id}
                              </Text>
                              {hotel.commision_rate && (
                                <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>
                                  Commission: {hotel.commision_rate}%
                                </Tag>
                              )}
                            </Space>
                          </Space>
                        }
                      />
                      <Divider style={{ margin: '16px 0' }} />
                      <Row gutter={16}>
                        <Col span={12}>
                          <Statistic
                            title="Total Rooms"
                            value={totalRooms}
                            valueStyle={{ fontSize: 16 }}
                          />
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="Available"
                            value={availableRooms}
                            valueStyle={{ fontSize: 16, color: availableRooms > 5 ? '#3f8600' : '#cf1322' }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Badge.Ribbon>
                </Col>
              );
            })}
          </Row>

          {filteredHotels.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  {searchTerm ? "No hotels match your search." : "No hotels are currently associated with your account."}
                </span>
              }
              style={{ marginTop: 60 }}
            />
          )}
        </>
      )}

      {/* Hotel Details Modal */}
      <Modal
        title={null}
        visible={!!selectedHotel}
        onCancel={() => setSelectedHotel(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setSelectedHotel(null)}>
            Close
          </Button>
        ]}
        width={1000}
        bodyStyle={{ padding: 0 }}
        centered
        destroyOnClose
      >
        {selectedHotel && (
          <div style={{ padding: 0 }}>
            {/* Modal Header/Hero */}
            <div style={{ height: 300, position: 'relative', overflow: 'hidden' }}>
              <img
                src={getHotelPrimaryImage(selectedHotel)}
                alt={selectedHotel.Hotel_name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={e => { e.target.src = DEFAULT_HOTEL_IMAGE; }}
              />
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '40px 24px 24px',
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                color: 'white'
              }}>
                <Title level={2} style={{ color: 'white', margin: 0 }}>{selectedHotel.Hotel_name}</Title>
                <Space size={12}>
                  <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                    <EnvironmentOutlined style={{ marginRight: 4 }} />
                    {selectedHotel.address}, {selectedHotel.city}
                  </Text>
                  {renderStars(selectedHotel.rating)}
                </Space>
              </div>
            </div>

            <div style={{ padding: '24px' }}>
              <Row gutter={32}>
                <Col xs={24} lg={16}>
                  <Tabs defaultActiveKey="overview" animated={{ tabPane: true }}>
                    <TabPane
                      tab={<span><InfoCircleOutlined />Overview</span>}
                      key="overview"
                    >
                      <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="Hotel ID">{selectedHotel.Hotel_Id}</Descriptions.Item>
                        <Descriptions.Item label="Status">
                          <Tag color={selectedHotel.status === 1 ? 'green' : 'red'}>
                            {selectedHotel.status === 1 ? 'Active' : 'Inactive'}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Hotel Type">{selectedHotel.hotelType || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Commission">{selectedHotel.commision_rate}%</Descriptions.Item>
                        {selectedHotel.phone && (
                          <Descriptions.Item label="Contact">
                            <Space><PhoneOutlined /> {selectedHotel.phone}</Space>
                          </Descriptions.Item>
                        )}
                        {selectedHotel.email && (
                          <Descriptions.Item label="Email">{selectedHotel.email}</Descriptions.Item>
                        )}
                      </Descriptions>

                      {selectedHotel.description && (
                        <div style={{ marginTop: 24 }}>
                          <Title level={4}>About the Property</Title>
                          <Paragraph type="secondary" style={{ fontSize: 15, lineHeight: '1.8' }}>
                            {selectedHotel.description}
                          </Paragraph>
                        </div>
                      )}

                      {selectedHotel.amenities && selectedHotel.amenities.length > 0 && (
                        <div style={{ marginTop: 24 }}>
                          <Title level={4}>Amenities</Title>
                          <Row gutter={[12, 12]}>
                            {selectedHotel.amenities.map((amenity, idx) => (
                              <Col key={idx} xs={12} sm={8}>
                                <div style={{
                                  padding: '8px 12px',
                                  background: '#f5f5f5',
                                  borderRadius: 6,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8
                                }}>
                                  {getAmenityIcon(amenity)}
                                  <Text style={{ fontSize: 13 }}>{amenity}</Text>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      )}
                    </TabPane>

                    <TabPane
                      tab={<span><AppstoreOutlined />Floor & Room Map</span>}
                      key="floors"
                    >
                      {selectedHotel.floors && selectedHotel.floors.length > 0 ? (
                        <List
                          itemLayout="vertical"
                          dataSource={selectedHotel.floors}
                          renderItem={(floor, index) => (
                            <List.Item key={index} style={{ padding: '16px 0' }}>
                              <Title level={5}>
                                <AppstoreAddOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                Floor {floor.floor_number || index + 1}
                              </Title>
                              <Row gutter={[8, 8]}>
                                {floor.rooms?.map((room, rIdx) => (
                                  <Col key={rIdx} xs={12} sm={8} md={6}>
                                    <Tooltip title={room.available ? "Available" : "Occupied"}>
                                      <div style={{
                                        padding: '10px',
                                        background: room.available ? '#f6ffed' : '#fff1f0',
                                        border: `1px solid ${room.available ? '#b7eb8f' : '#ffa39e'}`,
                                        borderRadius: 6,
                                        textAlign: 'center'
                                      }}>
                                        <Text strong style={{ display: 'block', fontSize: 13 }}>Room {room.room_number}</Text>
                                        <Badge status={room.available ? "success" : "error"} text={room.available ? "Available" : "Occupied"} />
                                      </div>
                                    </Tooltip>
                                  </Col>
                                ))}
                              </Row>
                            </List.Item>
                          )}
                        />
                      ) : (
                        <Empty description="No floor data available" />
                      )}
                    </TabPane>
                  </Tabs>
                </Col>

                <Col xs={24} lg={8}>
                  <Card
                    title="Room Summary"
                    style={{ background: '#fafafa', borderRadius: 12, border: '1px solid #f0f0f0' }}
                  >
                    {(() => {
                      const { totalRooms, availableRooms } = getRoomStats(selectedHotel);
                      const occupancy = totalRooms > 0 ? Math.round(((totalRooms - availableRooms) / totalRooms) * 100) : 0;
                      return (
                        <Space direction="vertical" size={24} style={{ width: '100%' }}>
                          <Statistic
                            title="Total Capacity"
                            value={totalRooms}
                            suffix="Rooms"
                          />
                          <Statistic
                            title="Current Availability"
                            value={availableRooms}
                            valueStyle={{ color: '#3f8600' }}
                          />
                          <div>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Occupancy Rate</Text>
                            <div style={{ height: 8, background: '#e8e8e8', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{
                                width: `${occupancy}%`,
                                height: '100%',
                                background: occupancy > 80 ? '#cf1322' : '#1890ff',
                                transition: 'width 0.3s'
                              }} />
                            </div>
                            <Text strong style={{ float: 'right', marginTop: 4 }}>{occupancy}%</Text>
                          </div>
                        </Space>
                      );
                    })()}
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Hotels;
